import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { HttpClient, HttpHeaders } from '@angular/common/http';

import { firstValueFrom, Observable } from 'rxjs';
import * as Sentry from '@sentry/browser';
import { TranslateService } from '@ngx-translate/core';

import { ErrorInterceptorSkipHeader } from '../common/functions-and-constant/interceptor';
import { URL_CONSTANTS } from '../common/utils/url';
import { URLService } from './dynamic-url.service';
import { TelemetryService } from './telemetry.service';
import { LOGIN_TYPE } from './auth.service';
import { ThirdPartyLoginService } from './third-party-login.service';

@Injectable({
  providedIn: 'root',
})
export class TutorCruncherLoginService {
  constructor(
    private readonly http: HttpClient,
    private readonly urlService: URLService,
    private readonly router: Router,
    private readonly telemetry: TelemetryService,
    private readonly translate: TranslateService,
    private readonly thirdPartyLoginService: ThirdPartyLoginService,
  ) {}

  private authenticateTutorCruncherUser(
    company_id: string,
    token: string,
  ): Observable<{ token: string; space: string; user: any }> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      [ErrorInterceptorSkipHeader]: '',
    });
    const url = `${this.urlService.getDynamicUrl()}/tutorcruncher/company-login`;
    const query = { company_id, token };
    return this.http.post<any>(url, {}, { headers, params: query });
  }

  public async attemptTutorCruncherSignIn(company_id: string, token: string): Promise<boolean> {
    try {
      const { token: userToken, space } = await firstValueFrom(
        this.authenticateTutorCruncherUser(company_id, token),
      );

      if (userToken) {
        this.loginTutorCruncherUser(userToken, space);
      }
      return false;
    } catch (error) {
      await this.thirdPartyLoginService.signOutForAPILogin();
      return this.handleTCErrors(error, company_id);
    }
  }

  private loginTutorCruncherUser(userToken: string, space: string) {
    const continueUrl = `${URL_CONSTANTS.SPACES}/${space || ''}`;

    this.router.navigate(['token-login'], {
      queryParams: {
        loginType: LOGIN_TYPE.TutorCruncherSignIn,
        token: userToken,
        continueUrl,
      },
    });
  }

  private handleTCErrors(error: any, company_id: string) {
    const { message, title } = this.getTCErrorMessage(error);
    this.reportError(error, company_id);
    return this.thirdPartyLoginService.displayCustomLoginErrorToasterAndRoute(message, title);
  }

  private reportError(error: any, company_id: string): void {
    // log Error To FullStory
    this.telemetry.event('tutorcruncher_sso_attempted', {
      target_institution_id: company_id,
      target_institution_name: error.error.institutionName || '',
      error,
    });

    // Capture Error with Sentry
    Sentry.captureException(error, {
      extra: {
        company_id,
        message: 'Tutor Cruncher sign in failed.',
      },
    });
  }

  private getTCErrorMessage(error: any) {
    switch (error.error.error_code) {
      case 'TUTORCRUNCHER_SIGNIN_USER_ALREADY_EXIST':
        return {
          message: this.translate.instant(
            'Sorry! You already have an account on Pencil Spaces. Please contact support to link your account to your institution',
          ),
          title: this.translate.instant('Account Already Exists'),
        };
      case 'TUTORCRUNCHER_SIGNIN_INVALID_TOKEN':
        return {
          message: this.translate.instant(
            'Sorry! It looks like you may have reset your integration. Please contact support to re-link your TutorCruncher account with Pencil Spaces.',
          ),
          title: this.translate.instant('Integration Reset Detected'),
        };
      case 'TUTORCRUNCHER_SIGNIN_NO_INTEGRATION':
        return {
          message: this.translate.instant(
            'Sorry! It looks like you have not properly configured this integration. Please contact support or visit the TutorCruncher integrations page to get started.',
          ),
          title: this.translate.instant('Integration Not Setup'),
        };
      default:
        return {
          message: this.translate
            .instant(`Sorry, you don't have an account, and we couldn't create one. Please contact your institution,
          Pencil Spaces support, or email support@pencilspaces.com. If your institution hasn't added Pencil Spaces, please request the integration.`),
          title: this.translate.instant('Account Sign In Failed'),
        };
    }
  }
}
